import type { LucideProps } from "lucide-react";
import { forwardRef } from "react";
import AnimatedGlyphHost from "./AnimatedGlyphHost";
import { ICON_REGISTRY, type IconEntry, type IconName } from "./registry";

/**
 * The icon size scale. Derived from measured demand, not invented: 14/16/20/24
 * were the sizes call sites actually used. `md` is the only default — the old
 * per-icon defaults were why an edit and a delete glyph rendered 2px apart in
 * the same row.
 */
export const ICON_SIZES = { lg: 24, md: 20, sm: 16, xs: 14 } as const;
export type IconSize = keyof typeof ICON_SIZES;

/**
 * Tones resolve to CSS variables rather than a `useTheme()` read, matching how
 * the rest of the design system colours things (see `BaseTypography`), so an
 * `Icon` costs no render-time theme subscription and follows light/dark for
 * free. The first four are the theme's own `palette.icon.*` namespace.
 */
const TONE_VARS = {
  active: "var(--mui-palette-action-selected)",
  danger: "var(--mui-palette-error-main)",
  default: "var(--mui-palette-icon-default)",
  inverse: "var(--mui-palette-icon-inverse)",
  muted: "var(--mui-palette-icon-muted)",
  strong: "var(--mui-palette-icon-strong)",
  success: "var(--mui-palette-success-main)",
  warning: "var(--mui-palette-warning-main)",
  white: "var(--mui-palette-common-white)"
} as const;
export type IconTone = keyof typeof TONE_VARS;

export interface IconProps extends Omit<
  LucideProps,
  "size" | "color" | "fill" | "ref"
> {
  /** What the icon means, not the glyph it draws. See `registry.ts`. */
  name: IconName;
  /**
   * Has no effect inside MUI buttons, icon buttons, toggles and menu items:
   * the theme renders icons there at `theme.controls.iconSize`.
   */
  size?: IconSize;
  /** Omit to inherit the parent's CSS `color` — which is how most icons work. */
  tone?: IconTone;
  /**
   * Accessible name. Omit for decorative icons (the default): an icon beside a
   * label, or inside a button that already has its own name. Only pass this
   * when the icon is the sole carrier of meaning, otherwise screen readers
   * announce it twice.
   */
  label?: string;
}

const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ label, name, size = "md", tone, ...rest }, ref) => {
    // Annotated: `as const satisfies` in the registry narrows each row to its
    // own literal type, so rows without `filled` do not carry the key at all.
    const { animated, filled, glyph: Glyph }: IconEntry = ICON_REGISTRY[name];
    const color = tone ? TONE_VARS[tone] : "currentColor";

    // A labelled icon is content; an unlabelled one is decoration and must be
    // hidden, not announced. `data-icon` is how the rendered DOM says which
    // icon it is: decorative icons are aria-hidden and so unreachable by role
    // or name, which leaves a test (or anyone in devtools) nothing to hold onto
    // — before this, tests asserted on a jest.mock's fake <span> instead of the
    // real component. Both branches below carry the same set, on whichever
    // element is outermost, so no consumer or selector can tell them apart.
    const shared = {
      "aria-hidden": label ? undefined : true,
      "aria-label": label,
      color,
      "data-icon": name,
      role: label ? "img" : undefined,
      size: ICON_SIZES[size]
    };

    if (animated) {
      return (
        <AnimatedGlyphHost ref={ref} glyph={animated} {...shared} {...rest} />
      );
    }

    return (
      <Glyph
        ref={ref}
        fill={filled ? color : "none"}
        // For IE/Edge legacy SVG focus behaviour; costs nothing.
        focusable="false"
        {...shared}
        {...rest}
      />
    );
  }
);

Icon.displayName = "Icon";

export default Icon;
